/**
 * Event Booking utilities.
 *
 * Event-style listings with fixed start/end times and limited capacity.
 *
 * Config stored in listing.publicData.event = {
 *   startDate: ISO string,
 *   endDate: ISO string,
 *   capacity: number,          — max attendees
 *   registrationDeadline: ISO  — booking closes after this date
 * }
 *
 * Attendee count tracked in listing.metadata.attendeeCount.
 */

/**
 * Get event config from listing.
 * @param {Object|null} listing
 * @returns {Object|null}
 */
export const getEventConfig = (listing) => {
  const event = listing?.attributes?.publicData?.event;
  if (!event?.startDate || !event?.capacity) return null;
  return event;
};

/**
 * Check if event registration is still open.
 * @param {Object} event — event config
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isRegistrationOpen = (event, now = new Date()) => {
  if (!event) return false;
  if (event.registrationDeadline && new Date(event.registrationDeadline) < now) return false;
  if (new Date(event.startDate) < now) return false;
  return true;
};

/**
 * Check if event has capacity available.
 * @param {Object} event
 * @param {number} currentAttendees
 * @returns {boolean}
 */
export const hasCapacity = (event, currentAttendees) => {
  if (!event?.capacity) return true;
  return currentAttendees < event.capacity;
};

/**
 * Get remaining spots.
 * @param {Object} event
 * @param {number} currentAttendees
 * @returns {number}
 */
export const getRemainingSpots = (event, currentAttendees) => {
  if (!event?.capacity) return Infinity;
  return Math.max(0, event.capacity - currentAttendees);
};

/**
 * Validate event config.
 * @param {Object} event
 * @returns {string|null}
 */
export const validateEventConfig = (event) => {
  if (!event.startDate) return 'Start date required';
  if (event.endDate && new Date(event.endDate) <= new Date(event.startDate)) return 'End date must be after start date';
  if (!event.capacity || event.capacity < 1) return 'Capacity must be at least 1';
  return null;
};
